import { Button, Dialog, DialogContent, DialogProps, useTheme } from '@material-ui/core';
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';
import { Form, Formik, FormikHelpers } from 'formik';
import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { DialogTitleWithFormStepper } from 'framework/dialogs/DialogTitleWithFormStepper';
import { FormDatePicker } from 'framework/forms/FormDatePicker';
import { FormTextField } from 'framework/forms/FormTextField';
import { PageableFormDialogActions } from 'framework/forms/PageableFormDialogActions';
import { handleFormResponse } from 'framework/forms/utils/handleFormResponse';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { useFormSubmit } from 'framework/hooks/useFormSubmit';
import { useSnackbarNotify } from 'framework/hooks/useSnackbarNotify';
import {
	CosiDegreeOfChange,
	CosiEndResult,
	cosiQuestionnaireCommand_evaluate,
	cosiQuestionnaireQuery_single,
	ICosiLineModel,
	ICosiQuestionnaireModel,
} from 'gen/ApiClient';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { CosiNeedForm } from './CosiNeedForm';
import { DragAndDropCosiNeedsTable } from './DragAndDropCosiNeedsTable';

const createSchema = (strings: IStrings) => {
	return yup
		.object<ICosiQuestionnaireModel>({
			rfiaId: yup.string().required(strings.formRequired(strings.requestForInsuranceAllowanceShort)),
			evaluationDate: yup.date().required(strings.formRequired(strings.evaluationDate)),
			lines: yup.mixed(),
			remarks: yup.string(),
		})
		.defined();
};

const stepsRecord: Record<number, (keyof ICosiQuestionnaireModel)[]> = {
	0: ['evaluationDate'],
	1: ['lines'],
	2: ['remarks'],
};

interface IProps extends DialogProps {
	confirm: VoidFunction;
	cancel: VoidFunction;
	rfiaId: string;
}

export const EvaluateCosiQuestionnaireForm = ({ confirm, cancel, rfiaId, ...rest }: IProps) => {
	const [cosiQuestionnaire] = useApiEffect(cosiQuestionnaireQuery_single, rfiaId);
	const theme = useTheme();
	const strings = useLocalization();
	const notify = useSnackbarNotify();
	const [step, setStep] = useState<number>(0);
	const [lines, setLines] = useState<ICosiLineModel[]>([]);
	const { open, cancel: closeNewNeed, isStacked } = useDialogsContext();
	const [submit, isSubmitting] = useFormSubmit(cosiQuestionnaireCommand_evaluate);

	const handleSubmit = async (values: ICosiQuestionnaireModel, helpers: FormikHelpers<ICosiQuestionnaireModel>) => {
		const r = await submit({ ...values, lines: lines.map((t, index) => ({ ...t, priority: index + 1 })) });
		if (handleFormResponse(r, helpers, stepsRecord, setStep)) {
			notify(strings.changedWhat(strings.cosiQuestionnaire));
			confirm();
		}
	};

	useEffect(() => {
		if (cosiQuestionnaire) {
			setLines(cosiQuestionnaire.lines);
		}
	}, [cosiQuestionnaire]);

	if (cosiQuestionnaire === undefined) {
		return <div></div>;
	}

	const onAdd = () => {
		open(
			<CosiNeedForm
				open
				confirm={onAddNeed}
				cancel={closeNewNeed}
			/>
		);
	};

	const onAddNeed = (need: string) => {
		const naDoc: CosiDegreeOfChange = 'NotApplicable';
		const naEr: CosiEndResult = 'NotApplicable';
		setLines([...lines, { description: need, degreeOfChange: naDoc, endResult: naEr, priority: 0 }]);
		closeNewNeed();
	};

	return (
		<Formik<ICosiQuestionnaireModel>
			validateOnMount
			initialValues={{
				rfiaId: rfiaId,
				lines: [],
				remarks: cosiQuestionnaire.remarks,
				evaluationDate: cosiQuestionnaire.evaluationDate,
			}}
			validationSchema={createSchema(strings)}
			onSubmit={handleSubmit}>
			<Form>
				<Dialog
					{...rest}
					scroll='paper'
					maxWidth='md'
					fullWidth={step === 1}
					open={rest.open && isStacked === false}>
					<DialogTitleWithFormStepper
						title={strings.cosiQuestionnaire}
						step={step}
						labels={[strings.date, strings.needs, strings.remarks]}
					/>
					<DialogContent
						className='df-col'
						dividers>
						{step === 0 && (
							<>
								<FormDatePicker<ICosiQuestionnaireModel>
									name='evaluationDate'
									label={strings.evaluationDate}
								/>
							</>
						)}
						{step === 1 && (
							<>
								<DragAndDropCosiNeedsTable
									ordered={lines}
									setOrdered={setLines}
									mustEvaluate={true}
								/>
								<div
									className='df-row-ac jc-e'
									style={{ padding: theme.spacing(1) }}>
									<Button
										startIcon={<PlaylistAddIcon />}
										color='primary'
										variant='contained'
										onClick={onAdd}>
										{strings.addWhat(strings.cosiNeed)}
									</Button>
								</div>
							</>
						)}
						{step === 2 && (
							<FormTextField<ICosiQuestionnaireModel>
								name='remarks'
								label={strings.remarks}
								multiline
							/>
						)}
					</DialogContent>
					<PageableFormDialogActions
						step={step}
						setStep={setStep}
						cancel={cancel}
						isSubmitting={isSubmitting}
						submitText={strings.create}
						schema={createSchema(strings)}
						stepsRecord={stepsRecord}
						canNext={step !== 1 || lines.length > 0}
					/>
				</Dialog>
			</Form>
		</Formik>
	);
};
